import {
  type CustomerBaseResponse,
  type UpdateUser,
  type User,
} from '@/features/account/services/User/types'
import { trackRegistrationCompleted, trackUserLoginError } from '@/features/shared/analytics/users'
import { getAuthClient } from '@/features/authentication/utils/authentication/client'
import { apiPatch, apiPost } from '@/features/shared/utils/dataFetching'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getErrorMessage, toError } from '@/features/shared/utils/dataFetching/utils'
import { fetchUserQuery, userProfileQueryOptions } from '@/features/account/services/User/queries'
import { userQueryOptions } from '@/features/account/services/User/queryOptions'
import { useGuestParams } from '@/features/account/services/GuestUser/hooks'
import { useMutationSelectStore } from '@/features/shop/services/ShoppingStore/mutations'
import { useMutationAddAddress } from '@/features/account/services/Addresses/mutations'
import { useLazyInitializedRef } from '@/features/shared/hooks/useLazyInitializedRef'
import { GuestHasCartQuery } from '@/features/shared/constants/ungatedParams'
import { getAnonymousId } from '@shipt/analytics-member-web'
import { useUserId } from '@/features/account/services/User/hooks'
import { checkUserEmail } from '@/features/account/services/User/fetchers'
import { getDataRightsSignUpValues } from '@/features/account/services/DataPrivacy/utils'
import { useMutationConsentPreferences } from '@/features/account/services/Preferences/mutations'
import { getTranscendConsentPurposes } from '@/lib/transcend/utils'
import { logInfo } from '@/features/shared/utils/logger'
import { useSearchParams } from 'next/navigation'
import { trackSegmentConsentPreferenceUpdated } from '@/features/shared/analytics/transcend'
import { clearGuestCookies } from '@/features/account/services/GuestUser/utils'

const CompleteGuestUserSignupMutationKey = 'Complete Guest User Signup Mutation'

export const useMutationUpdateUser = ({ awaitInvalidation = false } = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (user: UpdateUser) => {
      return apiPatch<CustomerBaseResponse>({
        config: { url: `/api/v1/customers/${user.id}`, data: user },
        fetcherName: 'useMutationUpdateUser',
      })
    },
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({
        queryKey: userProfileQueryOptions(id).queryKey,
      })
      const validation = queryClient.invalidateQueries({
        queryKey: userQueryOptions.queryKey,
      })
      if (awaitInvalidation) return validation
    },
    onError: (error) => {
      trackUserLoginError(toError(error))
    },
  })
}

export const useMutationSignUp = ({ onNameSubmit }: { onNameSubmit: () => void }) => {
  const searchParams = useSearchParams()
  const employeeId = searchParams?.get('employee_id')
  const queryClient = useQueryClient()
  const { mutate: updateConsentPreferences } = useMutationConsentPreferences()

  return useMutation({
    mutationFn: async (name: string) => {
      return apiPost<User>({
        config: {
          url: 'api/v2/customers',
          data: {
            ...getDataRightsSignUpValues(),
            name,
            universal_id: getAnonymousId(),
            ...(employeeId && { employee_id: String(employeeId) }),
          },
        },
        options: {
          ignoredMessages: ["Username can't be blank", 'Username has already been taken.'],
        },
        fetcherName: 'useMutationSignUp',
      })
    },
    onSuccess: async (userResponse) => {
      await getAuthClient().getAccessToken({ forceRefresh: true })
      await fetchUserQuery(queryClient)
      if (searchParams?.get('guest') !== GuestHasCartQuery) {
        clearGuestCookies(queryClient)
      }
      trackRegistrationCompleted('authv2')
      if (getTranscendConsentPurposes()) {
        trackSegmentConsentPreferenceUpdated()
        updateConsentPreferences({
          userId: userResponse.id,
          purposes: getTranscendConsentPurposes(),
        })
      } else {
        logInfo('Transcend consent purposes are not available on Signup.')
      }
    },
    onError: async (e) => {
      // This error can occur if the request times out client side,
      // while the backend successfully created the user. When this
      // happens, we should refetch the user data so they can continue
      // the welcome flow
      if (getErrorMessage(e) === 'Username has already been taken.') {
        await getAuthClient().getAccessToken({ forceRefresh: true })
        await fetchUserQuery(queryClient)
        if (searchParams?.get('guest') !== GuestHasCartQuery) {
          clearGuestCookies(queryClient)
        }
        onNameSubmit()
      }
    },
  })
}

export const useMutationUpdateDefaultAddress = () => {
  const userId = useUserId() ?? 0
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ addressId, overrideGroupId }: { addressId: number; overrideGroupId?: string }) =>
      apiPatch<{ id: number }>({
        config: {
          url: `api/v1/customers/${userId}`,
          data: { default_shopping_address_id: addressId },
        },
        options: {
          overrideGroupId,
        },
        fetcherName: 'useMutationUpdateDefaultAddress',
      }),
    onSuccess: () => {
      return queryClient.invalidateQueries({
        queryKey: userQueryOptions.queryKey,
      })
    },
  })
}

export const useCompleteGuestUserSignup = () => {
  const { mutateAsync: selectStore } = useMutationSelectStore()
  const { mutateAsync: updateDefaultAddress } = useMutationUpdateDefaultAddress()
  const { mutateAsync: addAddress } = useMutationAddAddress()
  const { guestAddress, guestStore } = useGuestParams() ?? {}
  const constantGuestStoreRef = useLazyInitializedRef(() => guestStore)
  const constantGuestAddressRef = useLazyInitializedRef(() => guestAddress)

  return useMutation({
    mutationKey: [CompleteGuestUserSignupMutationKey],
    mutationFn: async () => {
      if (!constantGuestAddressRef.current?.street1) return

      const addedAddress = await addAddress({
        address: {
          ...constantGuestAddressRef.current,
          require_validation: true,
        },
      })
      const customer = constantGuestStoreRef.current
        ? await selectStore({
            isGuestSignup: true,
            // skipPrefetch is set to true to avoid prefetching for the
            // CMS queries data since the user doesn't have an address set in
            // the user query cache yet and the prefetch is just an optimization.
            skipPrefetch: true,
            store: constantGuestStoreRef.current,
            addressId: addedAddress.id ?? 0,
          })
        : await updateDefaultAddress({ addressId: addedAddress.id ?? 0 })

      return { address: addedAddress, customer }
    },
  })
}

export const useMutationCheckUserEmail = () => useMutation({ mutationFn: checkUserEmail })
